import React, { useState, useRef } from 'react';
import { Plus, Search, X } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogClose } from './ui/dialog';
import { useTranslation } from '../hooks/useTranslation';

const MenuSection = ({ categories, onAddToCart }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState(categories[0]?.id);
  const [selectedItem, setSelectedItem] = useState(null);
  const { t, language } = useTranslation();
  const ordersEnabled = process.env.REACT_APP_ORDERS_ENABLED === 'true';
  
  // Refs for each category section
  const categoryRefs = useRef({});

  const handleAddToCart = (item) => {
    if (!ordersEnabled) {
      alert(t('ordersUnavailableMessage'));
      return;
    }
    onAddToCart(item);
  };

  // Category data with correct Latvian names
  const categoryData = {
    'maki-rolls': {
      emoji: '🍣',
      names: { en: 'Maki', lv: 'Maki', ru: 'Маки' }
    },
    'vegan-maki': {
      emoji: '🥬',
      names: { en: 'Vegetarian Maki', lv: 'Vegetāriskie Maki', ru: 'Вегетарианские Маки' }
    },
    'poke': {
      emoji: '🥗',
      names: { en: 'Poke', lv: 'Poke', ru: 'Поке' }
    },
    'desserts': {
      emoji: '🍨',
      names: { en: 'Desserts', lv: 'Deserti', ru: 'Десерты' }
    }
  };

  // Handle category click with smooth scroll
  const handleCategoryClick = (categoryId) => {
    setActiveCategory(categoryId);
    
    // Smooth scroll to category section
    const element = categoryRefs.current[categoryId];
    if (element) {
      const headerOffset = 120; // Account for sticky header
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  const filterItems = (items) => {
    if (!searchTerm) return items;
    return items.filter(
      (item) =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.description.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  // Get category display name
  const getCategoryDisplayName = (categoryId) => {
    const data = categoryData[categoryId];
    if (data && data.names[language]) {
      return data.names[language];
    }
    return categoryId;
  };

  // Get category emoji
  const getCategoryEmoji = (categoryId) => {
    return categoryData[categoryId]?.emoji || '🍱';
  };

  return (
    <section id="menu" className="py-24 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="inline-block px-6 py-3 bg-black text-white text-sm font-medium tracking-[0.2em] uppercase rounded-full mb-6">
            {t('ourMenu')}
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-black mb-4 sm:mb-6 tracking-tight">
            {t('exploreTitle')}
          </h2>
          <p className="text-gray-600 text-base sm:text-lg md:text-xl max-w-3xl mx-auto mb-6 sm:mb-10 leading-relaxed px-4">
            {t('menuDescription')}
          </p>

          {/* Search Bar */}
          <div className="max-w-xl mx-auto relative mb-8 sm:mb-12 px-4">
            <Search className="absolute left-8 sm:left-5 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              type="text"
              placeholder={t('searchMenu')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-12 sm:pl-14 h-12 sm:h-14 border-2 border-gray-200 focus:border-black transition-colors rounded-xl text-base sm:text-lg shadow-sm"
            />
          </div>
        </div>

        {/* Category Navigation Bar - Mobile Scrollable */}
        <div className="sticky top-0 z-40 bg-white/95 backdrop-blur-sm py-3 sm:py-4 mb-8 sm:mb-12">
          <div className="overflow-x-auto scrollbar-hide px-4">
            <div className="flex items-center gap-2 sm:gap-0 min-w-max sm:justify-center">
              <div className="flex items-center bg-gray-50 rounded-2xl p-1.5 sm:p-2 shadow-sm border border-gray-100">
                {categories.map((category, index) => {
                  const isActive = activeCategory === category.id;
                  const isLast = index === categories.length - 1;
                  
                  return (
                    <React.Fragment key={category.id}>
                      <button
                        onClick={() => handleCategoryClick(category.id)}
                        className={`
                          flex items-center gap-2 sm:gap-3 px-3 sm:px-6 py-2 sm:py-3 rounded-xl font-semibold
                          transition-all duration-300 ease-out text-sm sm:text-base
                          ${isActive 
                            ? 'bg-black text-white shadow-lg transform scale-105' 
                            : 'bg-transparent text-gray-700 hover:bg-gray-100 hover:text-black'
                          }
                        `}
                        data-testid={`category-btn-${category.id}`}
                      >
                        <span className="text-lg sm:text-xl">{getCategoryEmoji(category.id)}</span>
                        <span className="whitespace-nowrap">{getCategoryDisplayName(category.id)}</span>
                      </button>
                    
                    {/* Separator - hidden on mobile */}
                    {!isLast && (
                      <div className="hidden sm:block h-6 sm:h-8 w-px bg-gray-300 mx-1 sm:mx-2"></div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>
      </div>

        {/* Menu Items by Category */}
        <div className="space-y-10 sm:space-y-16 px-4 sm:px-0">
          {categories.map((category) => {
            const filteredItems = filterItems(category.items);
            
            return (
              <div 
                key={category.id} 
                ref={(el) => categoryRefs.current[category.id] = el}
                className="scroll-mt-24 sm:scroll-mt-32"
                data-testid={`category-section-${category.id}`}
              >
                {/* Category Header */}
                <div className="flex items-center gap-2 sm:gap-4 mb-4 sm:mb-8">
                  <span className="text-2xl sm:text-4xl">{getCategoryEmoji(category.id)}</span>
                  <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-black">
                    {getCategoryDisplayName(category.id)}
                  </h3>
                  <div className="flex-1 h-px bg-gradient-to-r from-gray-300 to-transparent"></div>
                </div>
                
                {/* Items Grid - Stack on mobile */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                  {filteredItems.length > 0 ? (
                    filteredItems.map((item) => (
                      <Card
                        key={item.id}
                        className="group overflow-hidden border border-gray-200 hover:border-black transition-all duration-500 hover:shadow-2xl bg-white transform hover:-translate-y-1"
                        data-testid={`menu-item-${item.id}`}
                      >
                        {/* Mobile: vertical layout, Desktop: horizontal */}
                        <div className="flex flex-col sm:flex-row h-full">
                          <div 
                            className="relative w-full sm:w-40 md:w-48 h-40 sm:h-40 md:h-48 flex-shrink-0 overflow-hidden cursor-pointer"
                            onClick={() => setSelectedItem(item)}
                          >
                            <img
                              src={item.image}
                              alt={item.name}
                              className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                              loading="lazy"
                              fetchPriority="high"
                              decoding="async"
                              style={{ 
                                imageRendering: '-webkit-optimize-contrast',
                                WebkitBackfaceVisibility: 'hidden',
                                backfaceVisibility: 'hidden'
                              }}
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                              <div className="bg-black/60 text-white px-3 py-1 rounded-full text-sm font-semibold">
                                {t('clickToView') || 'Click to view'}
                              </div>
                            </div>
                          </div>
                          <CardContent className="flex-1 p-4 sm:p-6 flex flex-col justify-between">
                            <div>
                              <h3 className="text-lg sm:text-xl font-bold text-black mb-1 sm:mb-2 group-hover:text-gray-700 transition-colors tracking-tight">
                                {item.nameTranslations?.[language] || item.name}
                              </h3>
                              <p className="text-gray-600 text-xs sm:text-sm leading-relaxed mb-2 line-clamp-2 sm:line-clamp-none">
                                {item.descriptionTranslations?.[language] || item.description}
                              </p>
                              {item.allergens && item.allergens.length > 0 && (
                                <p className="text-xs text-gray-500 italic mb-1 sm:mb-2 hidden sm:block">
                                  {t('allergens') || 'Allergens'}: {item.allergens.join(', ')}
                                </p>
                              )}
                              <p className="text-xl sm:text-2xl font-bold text-black mt-2 sm:mt-3">
                                €{item.price.toFixed(2)}
                              </p>
                            </div>
                            <div className="flex items-center justify-end mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-gray-100">
                              <Button
                                onClick={() => handleAddToCart(item)}
                                size="sm"
                                className="bg-black text-white hover:bg-gray-800 rounded-xl px-4 sm:px-5 py-2 transition-all duration-300 transform hover:scale-105 hover:shadow-lg text-sm sm:text-base"
                                data-testid={`add-to-cart-${item.id}`}
                              >
                                <Plus className="w-4 h-4 mr-2" />
                                {t('add')}
                              </Button>
                            </div>
                          </CardContent>
                        </div>
                      </Card>
                    ))
                  ) : (
                    <div className="col-span-full text-center py-12">
                      <p className="text-gray-500 text-lg">{t('noItemsFound')}</p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Image Preview Modal */}
        <Dialog open={selectedItem !== null} onOpenChange={() => setSelectedItem(null)}>
          <DialogContent className="max-w-4xl p-0 bg-white border-0">
            {selectedItem && (
              <div className="relative">
                <DialogClose className="absolute top-4 right-4 z-50 bg-white/90 hover:bg-white rounded-full p-2 transition-all shadow-lg">
                  <X className="w-6 h-6 text-black" />
                </DialogClose>
                
                <div className="grid md:grid-cols-2">
                  {/* Image Side */}
                  <div className="relative aspect-square bg-white overflow-hidden">
                    <img
                      src={selectedItem.image}
                      alt={selectedItem.nameTranslations?.[language] || selectedItem.name}
                      className="w-full h-full object-cover"
                      fetchPriority="high"
                      decoding="sync"
                      style={{ 
                        imageRendering: '-webkit-optimize-contrast',
                        WebkitBackfaceVisibility: 'hidden',
                        backfaceVisibility: 'hidden'
                      }}
                    />
                  </div>
                  
                  {/* Info Side */}
                  <div className="bg-white p-8 flex flex-col justify-between">
                    <div>
                      <h2 className="text-3xl font-bold text-black mb-4">
                        {selectedItem.nameTranslations?.[language] || selectedItem.name}
                      </h2>
                      <p className="text-gray-600 text-lg mb-6 leading-relaxed">
                        {selectedItem.descriptionTranslations?.[language] || selectedItem.description}
                      </p>
                      <p className="text-4xl font-bold text-black mb-8">
                        €{selectedItem.price.toFixed(2)}
                      </p>
                    </div>
                    
                    <Button
                      onClick={() => {
                        handleAddToCart(selectedItem);
                        setSelectedItem(null);
                      }}
                      className="w-full bg-black text-white hover:bg-gray-800 py-6 text-lg font-semibold rounded-xl"
                      data-testid="modal-add-to-cart"
                    >
                      <Plus className="w-5 h-5 mr-2" />
                      {t('add')}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </section>
  );
};

export default MenuSection;
